// Reusable hover tooltip with configurable delay and placement
// Uses position:fixed + createPortal to avoid overflow clipping from any scrollable ancestor
// (a plain `absolute` tooltip gets clipped by any ancestor with `overflow-y-auto`, since the
// CSS overflow spec computes the other axis to `auto` too — portaling to <body> sidesteps that).
import { useId, useState, useRef, useEffect, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

interface TooltipProps {
  content: string;
  children: ReactNode;
  delay?: number; // ms before tooltip appears after hover (default 1000)
  disabled?: boolean;
  className?: string;
  // 'bottom' (default) centers below the trigger; 'right' centers vertically to its right
  // (used for icon-only contexts, e.g. a collapsed sidebar item).
  placement?: 'bottom' | 'right';
}

export function Tooltip({
  content,
  children,
  delay = 1000,
  disabled = false,
  className,
  placement = 'bottom',
}: TooltipProps) {
  const tooltipId = useId();
  const [visible, setVisible] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0, width: 0 });
  const wrapperRef = useRef<HTMLDivElement>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearTimer = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  const measure = () => {
    if (!wrapperRef.current) return;
    const rect = wrapperRef.current.getBoundingClientRect();
    if (placement === 'right') {
      setCoords({ top: rect.top + rect.height / 2, left: rect.right + 8, width: rect.width });
    } else {
      setCoords({ top: rect.bottom + 8, left: rect.left, width: rect.width });
    }
  };

  const handleMouseEnter = () => {
    if (disabled) return;
    timerRef.current = setTimeout(() => {
      measure();
      setVisible(true);
    }, delay);
  };

  const handleFocus = () => {
    if (disabled) return;
    measure();
    setVisible(true);
  };

  const handleMouseLeave = () => {
    clearTimer();
    setVisible(false);
  };

  // Prevent stale timer on unmount
  useEffect(() => () => clearTimer(), []);

  return (
    <div
      ref={wrapperRef}
      className={className ?? 'w-full'}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onFocus={handleFocus}
      onBlur={handleMouseLeave}
    >
      <div aria-describedby={visible && !disabled ? tooltipId : undefined}>{children}</div>

      {visible &&
        !disabled &&
        createPortal(
          <div
            id={tooltipId}
            role="tooltip"
            style={
              placement === 'right'
                ? { top: coords.top, left: coords.left, transform: 'translateY(-50%)' }
                : { top: coords.top, left: coords.left, minWidth: Math.max(coords.width, 200) }
            }
            className={
              placement === 'right'
                ? 'hidden lg:block fixed z-9999 bg-[#19A2F8] text-white text-xs font-medium rounded-md px-2.5 py-1.5 pointer-events-none whitespace-nowrap'
                : 'hidden md:block fixed z-9999 bg-[#19A2F8] text-white text-sm font-medium rounded-xl px-4 py-3 pointer-events-none max-w-70'
            }
          >
            {placement === 'bottom' && (
              // Upward-pointing arrow
              <span
                className="absolute -top-1.75 left-5 block w-0 h-0
                  border-l-[7px] border-l-transparent
                  border-r-[7px] border-r-transparent
                  border-b-[7px] border-b-[#19A2F8]"
                aria-hidden="true"
              />
            )}
            {content}
          </div>,
          document.body
        )}
    </div>
  );
}
